import React from 'react';
import classNames from 'classnames';

import { Icon } from 'ui';

import getCustomerParcelShopMapUrl from '../utils';
import BranchPhoto from './BranchPhoto';
import OpeningHoursTable from './OpeningHoursTable';

type BranchLike = {
  name?: string | null;
  photo_url?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  address?: {
    street?: string | null;
    house_number?: string | null;
    city?: string | null;
    postal_code?: string | null;
    phone?: string | null;
    email?: string | null;
  } | null;
  opening_hours?: string[][][] | null;
};

type Props = {
  branch: BranchLike;
  photoSize?: 'sm' | 'md' | 'lg' | 'xl';
  showOpeningHours?: boolean;
  showContacts?: boolean;
  showMapLink?: boolean;
  /** When true, photo stacks above content on mobile and goes side-by-side on sm+ */
  stackOnMobile?: boolean;
  className?: string;
};

export function BranchMapLink({ url }: { url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="-mx-1 -my-0.5 inline-flex items-center gap-1 rounded px-1 py-0.5 text-xs font-medium text-indigo-700 hover:text-indigo-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
    >
      <Icon name="map" size="tiny" fitted />
      {pgettext('portal_i18n', 'Show on map')}
    </a>
  );
}

/**
 * Standardised "branch info" body used across portal, summary, ticket detail and selectors.
 *
 * Pass display flags to opt into opening hours, contact info, and map link. The wrapping card
 * (clickable, hover, etc.) is the caller's responsibility - this only renders the inner layout.
 */
function BranchInfo({
  branch,
  photoSize = 'md',
  showOpeningHours = false,
  showContacts = false,
  showMapLink = false,
  stackOnMobile = false,
  className,
}: Props) {
  const mapUrl = showMapLink ? getCustomerParcelShopMapUrl(branch) : null;

  return (
    <div className={classNames('flex items-start gap-4', stackOnMobile && 'flex-col sm:flex-row', className)}>
      <BranchPhoto
        src={branch.photo_url}
        alt={branch.name}
        size={photoSize}
        className={stackOnMobile ? 'h-48 w-full sm:h-32 sm:w-32' : undefined}
      />
      <div className="min-w-0 flex-1 space-y-2">
        <div>
          <div className="text-sm font-semibold text-gray-900 sm:text-base">{branch.name}</div>
          {branch.address && (
            <div className="text-xs text-gray-500">
              {branch.address.street}
              {branch.address.house_number ? ` ${branch.address.house_number}` : ''}
              {branch.address.street ? ', ' : ''}
              {branch.address.postal_code} {branch.address.city}
            </div>
          )}
        </div>

        {showOpeningHours && <OpeningHoursTable openingHours={branch.opening_hours} />}

        {showContacts && (branch.address?.phone || branch.address?.email) && (
          <div className="flex flex-col gap-1 text-sm text-gray-500 sm:flex-row sm:flex-wrap sm:gap-x-4">
            {branch.address?.phone && (
              <div className="flex items-center gap-1.5">
                <Icon name="phone" size="tiny" fitted solid color="gray" colorIntensity={400} />
                <span className="truncate">{branch.address.phone}</span>
              </div>
            )}
            {branch.address?.email && (
              <div className="flex items-center gap-1.5">
                <Icon name="mail" size="tiny" fitted solid color="gray" colorIntensity={400} />
                <span className="truncate">{branch.address.email}</span>
              </div>
            )}
          </div>
        )}

        {mapUrl && (
          <div>
            <BranchMapLink url={mapUrl} />
          </div>
        )}
      </div>
    </div>
  );
}

export default BranchInfo;
